import type { Chat } from '@trinity/chat';

type BuildMessageListParam = {
  selectedQueryIdentifier: string;
  queries: Record<string, Chat.Query>;
};

export function buildMessageQueries({
  selectedQueryIdentifier,
  queries,
}: BuildMessageListParam): Chat.Query[] {
  if (!selectedQueryIdentifier) return [];

  function getNodeItem(queryIdentifier: string): Chat.Query[] {
    if (!queryIdentifier) return [];

    const node = queries[queryIdentifier];

    if (!node) {
      console.error(
        `Could not find query '${queryIdentifier}' in the current state's queries tree. Either bad data or it's not being returned.`
      );
      return [];
    }

    const parents = getNodeItem(node.mapping.parentIdentifier);

    return [...parents, node];
  }

  return getNodeItem(selectedQueryIdentifier);
}

type GetLatestChildParams = {
  selectedQueryIdentifier: string;
  queries: Record<string, Chat.Query>;
};

export function getDeepestNode({ selectedQueryIdentifier, queries }: GetLatestChildParams): Chat.Query {
  function traverseNode(queryIdentifier: string): string {
    if (!queryIdentifier) return null;

    const node = queries[queryIdentifier];
    if (!node) {
      console.error(
        `Could not find query '${queryIdentifier}' in the current state's queries tree. Either bad data or it's not being returned.`
      );
    }
    if (!node.mapping.childrenIdentifiers.length) return node.identifier;
    const lastChildIdentifier = node.mapping.childrenIdentifiers[node.mapping.childrenIdentifiers.length - 1];
    return traverseNode(lastChildIdentifier);
  }

  const deepest = traverseNode(selectedQueryIdentifier);

  return queries[deepest];
}

type GetQuerySiblingsParams = {
  queryIdentifier: string;
  queries: Record<string, Chat.Query>;
};

export function getQuerySiblings({ queryIdentifier, queries }: GetQuerySiblingsParams): Chat.Query[] {
  if (!queryIdentifier) return [];

  const query = queries[queryIdentifier];
  if (!query) return [];

  const parentIdentifier = query.mapping.parentIdentifier;
  if (!parentIdentifier) {
    const noParentQueryIdentifiers = Object.keys(queries).filter(
      (identifier) => !queries[identifier].mapping.parentIdentifier
    );
    return noParentQueryIdentifiers
      .filter((identifier) => identifier !== query.identifier)
      .map((identifier) => queries[identifier])
      .filter(Boolean);
  }

  const parent = queries[parentIdentifier];
  if (!parent) return [];

  return parent.mapping.childrenIdentifiers
    .filter((identifier) => identifier !== query.identifier)
    .map((identifier) => queries[identifier])
    .filter(Boolean);
}
